import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { php } from '@codemirror/lang-php';
import { Toaster, toast } from 'sonner';
import { diffWords } from 'diff';
import {
  FolderOpen, File, FileCode, FileText, Server, Plus, Trash2, 
  RefreshCw, Save, Wand2, MessageSquare, Send, Loader2, Home,
  Eye, EyeOff, ChevronRight, ChevronDown, ChevronLeft, Sparkles,
  HelpCircle, Check, X, ArrowRight, Monitor, Smartphone, Undo2,
  FileType, Copy, Download, Settings, Zap, Lightbulb, Heart,
  Globe, Lock, User, FolderPlus, Edit3, Play, Search, Link,
  Image, FileSearch, AlertTriangle, CheckCircle, Info, ExternalLink,
  TrendingUp, Target, Tag, Type, AlertCircle, History, RotateCcw, Columns
} from 'lucide-react';
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "./components/ui/dialog";
import { ScrollArea } from "./components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strip HTML tags for text comparison
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return doc.body.textContent || '';
};

// Diff View Component
const DiffView = ({ original, modified, onAccept, onReject, accepting }) => {
  const [viewMode, setViewMode] = useState('diff'); // 'diff' | 'side'
  const originalText = stripHtml(original);
  const modifiedText = stripHtml(modified);
  const diff = diffWords(originalText, modifiedText);
  
  const addedChars = diff.filter(p => p.added).reduce((s, p) => s + p.value.length, 0);
  const removedChars = diff.filter(p => p.removed).reduce((s, p) => s + p.value.length, 0);

  return (
    <div className="mt-3 rounded-xl border border-indigo-200 overflow-hidden" data-testid="diff-view">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-indigo-50 border-b border-indigo-200">
        <div className="flex items-center gap-3">
          <Columns className="w-4 h-4 text-indigo-600" />
          <span className="font-medium text-sm text-indigo-700">Porównanie zmian</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">+{addedChars} znaków</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">-{removedChars} znaków</span>
        </div>
        <div className="flex gap-1 bg-white rounded-lg p-0.5 border">
          <button onClick={() => setViewMode('diff')} className={`px-2 py-1 text-xs rounded ${viewMode === 'diff' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500'}`} data-testid="diff-mode-inline">
            Zmiany
          </button>
          <button onClick={() => setViewMode('side')} className={`px-2 py-1 text-xs rounded ${viewMode === 'side' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500'}`} data-testid="diff-mode-side">
            Obok siebie
          </button>
        </div>
      </div>

      {/* Diff content */}
      {viewMode === 'diff' ? (
        <div className="p-4 max-h-72 overflow-auto bg-white text-sm leading-relaxed" data-testid="diff-inline">
          {diff.map((part, i) => (
            <span 
              key={i} 
              className={
                part.added ? 'bg-green-100 text-green-800 border-b border-green-300' : 
                part.removed ? 'bg-red-100 text-red-800 line-through' : 
                'text-gray-700'
              }
            >
              {part.value}
            </span>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 divide-x divide-indigo-100">
          <div className="p-3">
            <div className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1"><X className="w-3 h-3" /> Oryginał</div>
            <div className="text-sm text-gray-600 max-h-60 overflow-auto leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: original}} />
          </div>
          <div className="p-3">
            <div className="text-xs font-medium text-green-600 mb-2 flex items-center gap-1"><Check className="w-3 h-3" /> Po zmianach AI</div>
            <div className="text-sm text-gray-700 max-h-60 overflow-auto leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{__html: modified}} />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-indigo-200">
        <Button variant="outline" size="sm" onClick={onReject} className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="diff-reject">
          <X className="w-3 h-3" /> Odrzuć zmiany
        </Button>
        <Button size="sm" onClick={onAccept} disabled={accepting} className="gap-1 bg-green-600 hover:bg-green-700" data-testid="diff-accept">
          {accepting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Zastosuj na stronie
        </Button>
      </div>
    </div>
  );
};

// Content History Component
const ContentHistory = ({ postId, onRollback, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [postId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wp/content-history/${postId}`);
      setHistory(res.data.snapshots || []);
    } catch (e) {
      toast.error('Nie udało się załadować historii');
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (snapshotId) => {
    if (!window.confirm('Czy na pewno chcesz przywrócić tę wersję? Aktualna treść zostanie zapisana w historii.')) return;
    setRolling(snapshotId);
    try {
      await axios.post(`${API}/wp/rollback/${postId}?snapshot_id=${snapshotId}`);
      toast.success('Wersja przywrócona pomyślnie!');
      if (onRollback) onRollback();
    } catch (e) {
      toast.error('Nie udało się przywrócić wersji');
    } finally {
      setRolling(null);
    }
  };

  const changeTypeLabels = {
    'before_ai_seo': 'Przed optymalizacją SEO',
    'before_ai_readability': 'Przed poprawą czytelności',
    'before_ai_meta': 'Przed zmianą meta tagów',
    'before_manual_update': 'Przed ręczną edycją',
    'before_rollback': 'Przed cofnięciem zmian',
  };

  return (
    <div className="mt-3 rounded-xl border border-amber-200 overflow-hidden" data-testid="content-history">
      <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-amber-600" />
          <span className="font-medium text-sm text-amber-700">Historia zmian</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="w-3 h-3" />
        </Button>
      </div>
      <div className="p-3 max-h-60 overflow-auto bg-white">
        {loading ? (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-amber-500" /></div>
        ) : history.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-4">Brak zapisanych wersji. Historia będzie tworzona automatycznie przy każdej zmianie AI.</p>
        ) : (
          <div className="space-y-2">
            {history.map((snap) => (
              <div key={snap.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-amber-50 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-700 truncate">{snap.title || 'Bez tytułu'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-amber-600">{changeTypeLabels[snap.change_type] || snap.change_type}</span>
                    <span className="text-xs text-gray-400">{new Date(snap.created_at).toLocaleString('pl')}</span>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleRollback(snap.id)} 
                  disabled={rolling === snap.id}
                  className="gap-1 text-xs shrink-0 ml-2"
                  data-testid={`rollback-${snap.id}`}
                >
                  {rolling === snap.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Przywróć
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Login Screen Component
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem('wp_user', response.data.user);
        onLogin(response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 gradient-soft">
      <div className="max-w-md w-full animate-fadeIn">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-5 rounded-2xl gradient-primary flex items-center justify-center shadow-lg">
            <Sparkles className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="login-title">
            Edytor WP
          </h1>
          <p className="text-gray-500">Zaloguj się, aby edytować stronę</p>
        </div>

        <form onSubmit={handleLogin} className="card-friendly p-8 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Email</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                placeholder="Twój email"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Hasło</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
                placeholder="Twoje hasło"
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-big-friendly flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Logowanie...</>
            ) : (
              <><Lock className="w-5 h-5" /> Zaloguj się</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Get file icon with friendly colors
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  const iconMap = {
    'php': <FileCode className="w-5 h-5 text-purple-500" />,
    'js': <FileCode className="w-5 h-5 text-yellow-500" />,
    'css': <FileType className="w-5 h-5 text-blue-500" />,
    'html': <FileCode className="w-5 h-5 text-orange-500" />,
    'json': <FileText className="w-5 h-5 text-green-500" />,
    'txt': <FileText className="w-5 h-5 text-gray-500" />,
  };
  return iconMap[ext] || <File className="w-5 h-5 text-gray-400" />;
};

// Get friendly file type name
const getFriendlyTypeName = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  const nameMap = {
    'php': 'Skrypt PHP',
    'js': 'JavaScript',
    'css': 'Style (wygląd)',
    'html': 'Strona HTML',
    'json': 'Ustawienia',
    'txt': 'Notatka',
  };
  return nameMap[ext] || 'Plik';
};

// CodeMirror language extension
const getLanguageExtension = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  const langMap = {
    'php': php(),
    'js': javascript(),
    'jsx': javascript({ jsx: true }),
    'css': css(),
    'html': html(),
    'htm': html(),
  };
  return langMap[ext] || html();
};

// Welcome Screen Component
const WelcomeScreen = ({ onStart, connectionsCount }) => (
  <div className="min-h-screen flex items-center justify-center p-8 gradient-soft">
    <div className="max-w-2xl w-full text-center animate-fadeIn">
      <div className="mb-8">
        <div className="w-24 h-24 mx-auto mb-6 rounded-3xl gradient-primary flex items-center justify-center shadow-lg">
          <Sparkles className="w-12 h-12 text-white" />
        </div>
        <h1 className="text-4xl font-bold mb-4 text-gray-900">
          Witaj w Edytorze WP!
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Łatwy sposób na edycję i naprawianie Twojej strony WordPress.
          <br />Bez wiedzy technicznej - z pomocą sztucznej inteligencji.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="card-friendly p-6 text-left">
          <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
            <Globe className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="font-semibold mb-2">Połącz ze stroną</h3>
          <p className="text-sm text-gray-500">Bezpieczne połączenie z Twoim serwerem</p>
        </div>
        <div className="card-friendly p-6 text-left">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
            <Edit3 className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-semibold mb-2">Edytuj z podglądem</h3>
          <p className="text-sm text-gray-500">Widzisz zmiany na żywo</p>
        </div>
        <div className="card-friendly p-6 text-left">
          <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center mb-4">
            <Wand2 className="w-6 h-6 text-pink-600" />
          </div>
          <h3 className="font-semibold mb-2">AI Asystent</h3>
          <p className="text-sm text-gray-500">Pomoc w pisaniu i naprawianiu kodu</p>
        </div>
      </div>

      <button onClick={onStart} className="btn-big-friendly" data-testid="start-btn">
        {connectionsCount > 0 ? (
          <>Przejdź do panelu <ArrowRight className="w-5 h-5" /></>
        ) : (
          <>Rozpocznij konfigurację <ArrowRight className="w-5 h-5" /></>
        )}
      </button>

      {connectionsCount > 0 && (
        <p className="mt-4 text-gray-500">
          Masz {connectionsCount} zapisane połączenie
        </p>
      )}
    </div>
  </div>
);

// Connection Wizard Component
const ConnectionWizard = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    host: '',
    port: 22,
    username: '',
    password: '',
    protocol: 'sftp',
    remote_path: '/'
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/connections/test`, form);
      setTestResult(response.data);
      if (response.data.success) {
        setTimeout(() => setStep(3), 1000);
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Błąd połączenia. Sprawdź dane.' });
    } finally {
      setTesting(false);
    }
  };

  const saveConnection = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/connections`, form);
      toast.success('🎉 Połączenie zapisane!');
      onComplete();
    } catch (error) {
      toast.error('Błąd zapisu. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="max-w-xl w-full animate-fadeIn">
        {/* Progress */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all
                ${step >= s ? 'bg-indigo-600 text-white shadow-lg' : 'bg-gray-200 text-gray-500'}`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 mx-2 rounded transition-all
                  ${step > s ? 'bg-indigo-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <Card className="card-friendly p-8">
          {step === 1 && (
            <div className="animate-fadeIn">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-indigo-100 flex items-center justify-center">
                  <Globe className="w-8 h-8 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Dane Twojego serwera</h2>
                <p className="text-gray-500">Podaj informacje o serwerze, na którym jest Twoja strona</p>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Nazwa (dla Ciebie)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>Dowolna nazwa, np. "Moja strona"</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Input
                    data-testid="wizard-name"
                    placeholder="np. Moja strona WordPress"
                    value={form.name}
                    onChange={(e) => setForm({...form, name: e.target.value})}
                    className="h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Adres serwera (host)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent>Znajdziesz to w panelu hostingu</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Input
                    data-testid="wizard-host"
                    placeholder="np. ftp.twojadomena.pl"
                    value={form.host}
                    onChange={(e) => setForm({...form, host: e.target.value})}
                    className="h-12 rounded-xl"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Port</Label>
                    <Input
                      data-testid="wizard-port"
                      type="number"
                      value={form.port}
                      onChange={(e) => setForm({...form, port: parseInt(e.target.value)})}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Typ połączenia</Label>
                    <Select value={form.protocol} onValueChange={(v) => setForm({...form, protocol: v, port: v === 'sftp' ? 22 : 21})}>
                      <SelectTrigger className="h-12 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sftp">SFTP (bezpieczne)</SelectItem>
                        <SelectItem value="ftp">FTP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={onCancel} className="h-12 px-6">
                  <ChevronLeft className="w-4 h-4 mr-2" /> Wstecz
                </Button>
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!form.name || !form.host}
                  className="h-12 px-8 rounded-full"
                  data-testid="wizard-next-1"
                >
                  Dalej <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fadeIn">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-100 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Dane logowania</h2>
                <p className="text-gray-500">Login i hasło do serwera FTP/SFTP</p>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    <User className="w-4 h-4 inline mr-1" /> Nazwa użytkownika
                  </Label>
                  <Input
                    data-testid="wizard-username"
                    placeholder="Twój login FTP"
                    value={form.username}
                    onChange={(e) => setForm({...form, username: e.target.value})}
                    className="h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    <Lock className="w-4 h-4 inline mr-1" /> Hasło
                  </Label>
                  <Input
                    data-testid="wizard-password"
                    type="password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({...form, password: e.target.value})}
                    className="h-12 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Folder startowy (opcjonalnie)
                  </Label>
                  <Input
                    data-testid="wizard-path"
                    placeholder="/public_html"
                    value={form.remote_path}
                    onChange={(e) => setForm({...form, remote_path: e.target.value})}
                    className="h-12 rounded-xl"
                  />
                </div>

                {testResult && (
                  <div className={`p-4 rounded-xl flex items-center gap-3 animate-fadeIn
                    ${testResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {testResult.success ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                    <span className="font-medium">{testResult.message}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between mt-8">
                <Button variant="ghost" onClick={() => setStep(1)} className="h-12 px-6">
                  <ChevronLeft className="w-4 h-4 mr-2" /> Wstecz
                </Button>
                <Button 
                  onClick={testConnection} 
                  disabled={!form.username || !form.password || testing}
                  className="h-12 px-8 rounded-full"
                  data-testid="wizard-test"
                >
                  {testing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sprawdzam...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" /> Sprawdź połączenie</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fadeIn text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center animate-bounce-soft">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-green-700">Połączenie działa!</h2>
              <p className="text-gray-500 mb-8">Wszystko gotowe. Zapisz połączenie, żeby móc go użyć później.</p>

              <div className="p-4 rounded-xl bg-gray-50 mb-8 text-left">
                <div className="flex items-center gap-3 text-sm">
                  <Server className="w-5 h-5 text-indigo-500" />
                  <div>
                    <p className="font-semibold">{form.name}</p>
                    <p className="text-gray-500">{form.host}</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={saveConnection} 
                disabled={saving}
                className="h-14 px-10 rounded-full text-lg"
                data-testid="wizard-save"
              >
                {saving ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Zapisuję...</>
                ) : (
                  <><Save className="w-5 h-5 mr-2" /> Zapisz i rozpocznij</>
                )}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ connections, onSelectConnection, onAddConnection, onDeleteConnection, user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('wordpress');
  const [wpPosts, setWpPosts] = useState([]);
  const [wpPages, setWpPages] = useState([]);
  const [auditResults, setAuditResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [auditDetail, setAuditDetail] = useState(null);
  const [aiFixLoading, setAiFixLoading] = useState(null);
  const [aiFix, setAiFix] = useState(null);
  const [aiMeta, setAiMeta] = useState(null);
  const [autoFixingAudit, setAutoFixingAudit] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [applyingDiff, setApplyingDiff] = useState(false);

  useEffect(() => {
    if (activeTab === 'wordpress') loadWPContent();
    if (activeTab === 'audit') runAudit();
  }, [activeTab]);

  const loadWPContent = async () => {
    setLoading(true);
    try {
      const [postsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/wp/posts?per_page=20${searchQuery ? `&search=${searchQuery}` : ''}`),
        axios.get(`${API}/wp/pages?per_page=20${searchQuery ? `&search=${searchQuery}` : ''}`)
      ]);
      setWpPosts(postsRes.data.items);
      setWpPages(pagesRes.data.items);
    } catch (err) {
      toast.error('Błąd ładowania treści WordPress');
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setLoading(true);
    setAuditResults(null);
    try {
      const res = await axios.post(`${API}/seo/audit-site`);
      setAuditResults(res.data);
    } catch (err) {
      toast.error('Błąd audytu SEO');
    } finally {
      setLoading(false);
    }
  };

  const auditSinglePage = async (url) => {
    setAuditDetail(null);
    try {
      const res = await axios.post(`${API}/seo/audit-page?url=${encodeURIComponent(url)}`);
      setAuditDetail(res.data);
    } catch (err) {
      toast.error('Błąd skanowania strony');
    }
  };

  const getAIFix = async (url, issue) => {
    setAiFixLoading(issue);
    setAiFix(null);
    try {
      const res = await axios.post(`${API}/seo/ai-fix?url=${encodeURIComponent(url)}&issue=${encodeURIComponent(issue)}`);
      setAiFix(res.data.fix);
    } catch (err) {
      toast.error('Błąd AI - spróbuj ponownie');
    } finally {
      setAiFixLoading(null);
    }
  };

  const scoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const severityIcon = (s) => {
    if (s === 'critical') return <AlertCircle className="w-4 h-4 text-red-500" />;
    if (s === 'warning') return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return <Info className="w-4 h-4 text-blue-500" />;
  };

  const tabs = [
    { id: 'wordpress', label: 'Wpisy i strony', icon: FileText },
    { id: 'files', label: 'Pliki kodu', icon: FileCode },
    { id: 'audit', label: 'Audyt SEO', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50" data-testid="dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">SEO Editor</h1>
              <p className="text-xs text-gray-400">kurdynowski.com.pl</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{user}</span>
            <Button variant="ghost" onClick={onLogout} className="gap-1 text-gray-500 hover:text-red-500" data-testid="dashboard-logout-btn">
              <X className="w-4 h-4" /> Wyloguj
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* WordPress Content Tab */}
        {activeTab === 'wordpress' && (
          <div className="animate-fadeIn">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadWPContent()}
                  placeholder="Szukaj wpisów i stron..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  data-testid="wp-search"
                />
              </div>
              <Button onClick={loadWPContent} variant="outline" className="gap-2" data-testid="wp-refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Odśwież
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" /><p className="text-gray-500 mt-3">Ładowanie treści...</p></div>
            ) : (
              <>
                {/* Posts */}
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-500" /> Wpisy bloga ({wpPosts.length})</h2>
                <div className="grid gap-3 mb-8">
                  {wpPosts.map(post => (
                    <div key={post.id} className="card-friendly p-4 flex items-center justify-between hover-lift cursor-pointer" onClick={() => setSelectedPost(post)} data-testid={`wp-post-${post.id}`}>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm" dangerouslySetInnerHTML={{__html: post.title}} />
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span>{new Date(post.date).toLocaleDateString('pl')}</span>
                          <a href={post.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-500 hover:underline" onClick={e => e.stopPropagation()}>
                            <ExternalLink className="w-3 h-3" /> Zobacz
                          </a>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); auditSinglePage(post.link); }} className="gap-1 text-xs" data-testid={`audit-post-${post.id}`}>
                        <TrendingUp className="w-3 h-3" /> SEO
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Pages */}
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Globe className="w-5 h-5 text-emerald-500" /> Strony ({wpPages.length})</h2>
                <div className="grid gap-3">
                  {wpPages.map(page => (
                    <div key={page.id} className="card-friendly p-4 flex items-center justify-between hover-lift cursor-pointer" onClick={() => setSelectedPost(page)} data-testid={`wp-page-${page.id}`}>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-sm" dangerouslySetInnerHTML={{__html: page.title}} />
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span>{new Date(page.date).toLocaleDateString('pl')}</span>
                          <a href={page.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-500 hover:underline" onClick={e => e.stopPropagation()}>
                            <ExternalLink className="w-3 h-3" /> Zobacz
                          </a>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); auditSinglePage(page.link); }} className="gap-1 text-xs" data-testid={`audit-page-${page.id}`}>
                        <TrendingUp className="w-3 h-3" /> SEO
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Post detail / SEO audit dialog */}
            {selectedPost && (
              <Dialog open={!!selectedPost} onOpenChange={() => { setSelectedPost(null); setAiFix(null); setAiMeta(null); setShowHistory(false); setShowDiff(false); }}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle dangerouslySetInnerHTML={{__html: selectedPost.title}} />
                    <DialogDescription>
                      <a href={selectedPost.link} target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {selectedPost.link}
                      </a>
                    </DialogDescription>
                  </DialogHeader>

                  {/* AI Actions */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button onClick={async () => {
                      if (!window.confirm('Czy na pewno chcesz zoptymalizować SEO tego wpisu? AI zachowa treść i poprawi strukturę. Zmiany zostaną zapisane na stronie.')) return;
                      setAiFixLoading('seo');
                      setAiMeta(null);
                      setShowDiff(false);
                      try {
                        const res = await axios.post(`${API}/wp/auto-fix?post_id=${selectedPost.id}&action=seo`);
                        if (res.data.saved) {
                          setAiFix(res.data.result);
                          setShowDiff(true);
                          toast.success('SEO zoptymalizowane i zapisane na stronie!');
                        } else if (res.data.save_error) {
                          setAiFix(res.data.result);
                          setShowDiff(true);
                          toast.warning(res.data.save_error);
                        }
                      } catch(e) { toast.error('Błąd AI - spróbuj ponownie'); }
                      finally { setAiFixLoading(null); }
                    }} disabled={!!aiFixLoading} className="gap-2 text-sm bg-green-600 hover:bg-green-700" data-testid="ai-seo-optimize">
                      {aiFixLoading === 'seo' ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />} Optymalizuj SEO i zapisz
                    </Button>
                    <Button variant="outline" onClick={async () => {
                      setAiFixLoading('readability');
                      setAiMeta(null);
                      setShowDiff(false);
                      try {
                        const res = await axios.post(`${API}/wp/ai-improve?post_id=${selectedPost.id}&action=readability`);
                        if (res.data.success) {
                          if (!res.data.is_safe) {
                            toast.warning('Uwaga: wynik AI jest krótszy niż oryginał. Sprawdź porównanie zmian.');
                          } else {
                            toast.info('Sprawdź porównanie zmian poniżej i zaakceptuj lub odrzuć');
                          }
                          setAiFix(res.data.result);
                          setShowDiff(true);
                        }
                      } catch(e) { toast.error('Błąd AI'); }
                      finally { setAiFixLoading(null); }
                    }} disabled={!!aiFixLoading} className="gap-2 text-sm" data-testid="ai-readability">
                      {aiFixLoading === 'readability' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Popraw czytelność
                    </Button>
                    <Button variant="outline" onClick={async () => {
                      setAiFixLoading('meta');
                      setAiFix(null);
                      setShowDiff(false);
                      try {
                        const res = await axios.post(`${API}/wp/ai-improve?post_id=${selectedPost.id}&action=meta`);
                        if (res.data.success && res.data.result_type === 'meta') {
                          setAiMeta(res.data.meta);
                          toast.success('Meta tagi wygenerowane! Sprawdź i zastosuj.');
                        } else {
                          toast.error('Nie udało się wygenerować meta tagów');
                        }
                      } catch(e) { toast.error('Błąd AI'); }
                      finally { setAiFixLoading(null); }
                    }} disabled={!!aiFixLoading} className="gap-2 text-sm" data-testid="ai-meta-tags">
                      {aiFixLoading === 'meta' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Generuj meta tagi
                    </Button>
                    <Button variant="outline" onClick={() => auditSinglePage(selectedPost.link)} className="gap-2 text-sm" data-testid="audit-selected-post">
                      <Search className="w-4 h-4" /> Audyt SEO
                    </Button>
                    <Button variant="outline" onClick={() => { setShowHistory(!showHistory); setShowDiff(false); }} className={`gap-2 text-sm ${showHistory ? 'bg-amber-50 text-amber-700 border-amber-300' : ''}`} data-testid="show-history">
                      <History className="w-4 h-4" /> Historia / Cofnij
                    </Button>
                  </div>

                  {/* Content History */}
                  {showHistory && (
                    <ContentHistory 
                      postId={selectedPost.id} 
                      onRollback={() => {
                        loadWPContent();
                        setShowHistory(false);
                      }}
                      onClose={() => setShowHistory(false)}
                    />
                  )}

                  {/* Meta Tags Result (separate from content) */}
                  {aiMeta && (
                    <div className="mt-3 p-4 rounded-xl bg-blue-50 border border-blue-200" data-testid="meta-result">
                      <h4 className="font-medium text-blue-700 flex items-center gap-2 mb-3"><Tag className="w-4 h-4" /> Wygenerowane meta tagi</h4>
                      <div className="space-y-2">
                        <div className="p-3 bg-white rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Tytuł (title)</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${(aiMeta.title?.length || 0) <= 60 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {aiMeta.title?.length || 0}/60
                            </span>
                          </div>
                          <p className="font-medium text-blue-600 text-sm">{aiMeta.title}</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500">Opis (description)</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${(aiMeta.description?.length || 0) <= 160 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {aiMeta.description?.length || 0}/160
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">{aiMeta.description}</p>
                        </div>
                        {aiMeta.keywords && (
                          <div className="p-3 bg-white rounded-lg">
                            <span className="text-xs text-gray-500">Słowa kluczowe</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(Array.isArray(aiMeta.keywords) ? aiMeta.keywords : [aiMeta.keywords]).map((kw, i) => (
                                <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">{kw}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={async () => {
                          if (!window.confirm('Czy na pewno zastosować meta tagi? Zaktualizuje to TYLKO tytuł i opis wpisu, NIE treść.')) return;
                          try {
                            const params = new URLSearchParams({ post_id: selectedPost.id });
                            if (aiMeta.title) params.append('title', aiMeta.title);
                            if (aiMeta.description) params.append('description', aiMeta.description);
                            await axios.post(`${API}/wp/apply-meta?${params.toString()}`);
                            toast.success('Meta tagi zastosowane na stronie!');
                          } catch(e) { toast.error('Błąd zapisu meta tagów'); }
                        }} className="gap-1 bg-blue-600 hover:bg-blue-700" data-testid="apply-meta-tags">
                          <Save className="w-3 h-3" /> Zastosuj meta tagi
                        </Button>
                        <span className="text-xs text-gray-500 self-center">Zmieni tylko tytuł i opis, NIE treść wpisu</span>
                      </div>
                    </div>
                  )}

                  {/* Diff View for content changes */}
                  {aiFix && showDiff && (
                    <DiffView 
                      original={selectedPost.content}
                      modified={aiFix}
                      accepting={applyingDiff}
                      onAccept={async () => {
                        setApplyingDiff(true);
                        try {
                          await axios.post(`${API}/wp/update`, { 
                            post_id: selectedPost.id, 
                            content: aiFix,
                            post_type: selectedPost.type === 'page' ? 'pages' : 'posts'
                          });
                          toast.success('Zmiany treści zapisane na stronie!');
                          setShowDiff(false);
                        } catch(e) { toast.error('Błąd zapisu'); }
                        finally { setApplyingDiff(false); }
                      }}
                      onReject={() => {
                        setAiFix(null);
                        setShowDiff(false);
                        toast.info('Zmiany odrzucone - oryginalna treść bez zmian');
                      }}
                    />
                  )}

                  {/* Fallback: AI Content Result without diff */}
                  {aiFix && !showDiff && (
                    <div className="mt-3 p-4 rounded-xl bg-purple-50 border border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-purple-700 flex items-center gap-2"><Wand2 className="w-4 h-4" /> Podgląd poprawionej treści</h4>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setShowDiff(true)} className="gap-1 text-xs" data-testid="show-diff-btn">
                            <Columns className="w-3 h-3" /> Porównaj zmiany
                          </Button>
                          <Button size="sm" onClick={async () => {
                            if (!window.confirm('Czy na pewno zastosować te zmiany treści na stronie?')) return;
                            try {
                              await axios.post(`${API}/wp/update`, { 
                                post_id: selectedPost.id, 
                                content: aiFix,
                                post_type: selectedPost.type === 'page' ? 'pages' : 'posts'
                              });
                              toast.success('Zmiany treści zapisane na stronie!');
                            } catch(e) { toast.error('Błąd zapisu'); }
                          }} className="gap-1 bg-green-600 hover:bg-green-700" data-testid="apply-ai-fix">
                            <Save className="w-3 h-3" /> Zastosuj na stronie
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-60 overflow-auto bg-white p-3 rounded-lg border" dangerouslySetInnerHTML={{__html: aiFix}} />
                    </div>
                  )}

                  {/* Content preview */}
                  <div className="mt-3 prose prose-sm max-w-none max-h-96 overflow-auto p-4 bg-gray-50 rounded-xl" dangerouslySetInnerHTML={{__html: selectedPost.content}} />
                </DialogContent>
              </Dialog>
            )}

            {/* SEO audit detail dialog */}
            {auditDetail && (
              <Dialog open={!!auditDetail} onOpenChange={() => { setAuditDetail(null); setAiFix(null); setAiMeta(null); }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <span>Analiza SEO</span>
                      <span className={`text-lg font-bold px-3 py-1 rounded-full border ${scoreColor(auditDetail.score)}`}>
                        {auditDetail.score}/100
                      </span>
                    </DialogTitle>
                    <DialogDescription>{auditDetail.url}</DialogDescription>
                  </DialogHeader>
                  {/* Auto-fix button */}
                  <div className="flex gap-2 mt-2">
                    <Button 
                      onClick={async () => {
                        if (!window.confirm('AI zoptymalizuje SEO tego wpisu i zapisze zmiany na stronie. Kontynuować?')) return;
                        setAutoFixingAudit(auditDetail.url);
                        try {
                          const res = await axios.post(`${API}/wp/auto-fix-from-audit?url=${encodeURIComponent(auditDetail.url)}`);
                          if (res.data.saved) {
                            toast.success(`SEO naprawione i zapisane: ${res.data.post_title || auditDetail.url}`);
                          } else if (res.data.save_error) {
                            toast.warning(res.data.save_error);
                          }
                        } catch(e) { 
                          const msg = e.response?.data?.detail || 'Błąd naprawy';
                          toast.error(msg); 
                        }
                        finally { setAutoFixingAudit(null); }
                      }} 
                      disabled={!!autoFixingAudit} 
                      className="gap-2 bg-green-600 hover:bg-green-700"
                      data-testid="auto-fix-audit"
                    >
                      {autoFixingAudit === auditDetail.url ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} 
                      Napraw automatycznie
                    </Button>
                    <span className="text-xs text-gray-500 self-center">AI poprawi SEO i zapisze na stronie</span>
                  </div>
                  <div className="space-y-3 mt-4">
                    {auditDetail.issues.map((issue, i) => (
                      <div key={i} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                        <div className="flex items-start gap-3">
                          {severityIcon(issue.severity)}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{issue.issue}</p>
                            <p className="text-xs text-gray-500 mt-1">{issue.fix}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => getAIFix(auditDetail.url, issue.issue)}
                            disabled={aiFixLoading === issue.issue}
                            className="gap-1 text-xs shrink-0"
                            data-testid={`ai-fix-${i}`}
                          >
                            {aiFixLoading === issue.issue ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                            AI Porada
                          </Button>
                        </div>
                      </div>
                    ))}
                    {auditDetail.issues.length === 0 && (
                      <div className="text-center py-8"><CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" /><p className="font-medium">Strona wygląda dobrze!</p></div>
                    )}
                  </div>
                  {aiFix && (
                    <div className="mt-4 p-4 rounded-xl bg-purple-50 border border-purple-200">
                      <h4 className="font-medium text-purple-700 mb-2 flex items-center gap-2"><Wand2 className="w-4 h-4" /> Porada AI</h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{aiFix}</div>
                    </div>
                  )}
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center text-xs">
                    <div className="p-3 bg-gray-50 rounded-xl"><div className="font-bold text-lg">{auditDetail.stats?.word_count || 0}</div>Słów</div>
                    <div className="p-3 bg-gray-50 rounded-xl"><div className="font-bold text-lg">{auditDetail.stats?.images || 0}</div>Obrazów</div>
                    <div className="p-3 bg-gray-50 rounded-xl"><div className="font-bold text-lg">{auditDetail.stats?.internal_links || 0}</div>Linków wewn.</div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <div className="animate-fadeIn">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {connections.map((conn, index) => (
                <div key={conn.id} className="card-friendly p-6 hover-lift animate-fadeIn" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center">
                      <Server className="w-7 h-7 text-white" />
                    </div>
                    {conn.id !== 'default-kurdynowski' && (
                      <button onClick={() => onDeleteConnection(conn.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={`delete-${conn.id}`}>
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{conn.name}</h3>
                  <p className="text-sm text-gray-500 mb-4 font-mono">{conn.host}</p>
                  <Button onClick={() => onSelectConnection(conn)} className="w-full h-11 rounded-xl" data-testid={`select-${conn.id}`}>
                    <FileCode className="w-4 h-4 mr-2" /> Edytuj pliki
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEO Audit Tab */}
        {activeTab === 'audit' && (
          <div className="animate-fadeIn">
            {loading ? (
              <div className="text-center py-16"><Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto" /><p className="text-gray-500 mt-4">Skanowanie strony kurdynowski.com.pl...</p><p className="text-xs text-gray-400 mt-1">To może potrwać do minuty</p></div>
            ) : auditResults ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className={`card-friendly p-6 text-center border-2 ${scoreColor(auditResults.average_score)}`}>
                    <div className="text-4xl font-bold">{auditResults.average_score}</div>
                    <div className="text-sm mt-1">Średni wynik SEO</div>
                  </div>
                  <div className="card-friendly p-6 text-center">
                    <div className="text-4xl font-bold text-gray-800">{auditResults.pages_audited}</div>
                    <div className="text-sm text-gray-500 mt-1">Stron przeskanowanych</div>
                  </div>
                  <div className="card-friendly p-6 text-center">
                    <div className="text-4xl font-bold text-orange-600">{auditResults.total_issues}</div>
                    <div className="text-sm text-gray-500 mt-1">Problemów znalezionych</div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Wyniki stron</h2>
                  <Button onClick={runAudit} variant="outline" className="gap-2" data-testid="rerun-audit">
                    <RefreshCw className="w-4 h-4" /> Skanuj ponownie
                  </Button>
                </div>

                <div className="space-y-3">
                  {auditResults.results.map((r, i) => (
                    <div key={i} className="card-friendly p-4 hover-lift cursor-pointer" onClick={() => auditSinglePage(r.url)} data-testid={`audit-result-${i}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm" dangerouslySetInnerHTML={{__html: r.title}} />
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{r.url}</p>
                          {r.issues.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {r.issues.map((issue, j) => (
                                <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex items-center gap-1">
                                  {severityIcon(issue.severity)} {issue.issue.substring(0, 40)}...
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className={`text-lg font-bold px-3 py-1 rounded-full border ml-4 shrink-0 ${scoreColor(r.score)}`}>
                          {r.score}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Audyt SEO</h3>
                <p className="text-gray-500 mb-6">Przeskanuj stronę kurdynowski.com.pl</p>
                <Button onClick={runAudit} className="gap-2" data-testid="start-audit"><Search className="w-4 h-4" /> Rozpocznij audyt</Button>
              </div>
            )}

            {/* Audit detail dialog reuse */}
            {auditDetail && (
              <Dialog open={!!auditDetail} onOpenChange={() => { setAuditDetail(null); setAiFix(null); setAiMeta(null); }}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      Analiza SEO
                      <span className={`text-lg font-bold px-3 py-1 rounded-full border ${scoreColor(auditDetail.score)}`}>{auditDetail.score}/100</span>
                    </DialogTitle>
                    <DialogDescription>{auditDetail.url}</DialogDescription>
                  </DialogHeader>
                  {/* Auto-fix button */}
                  <div className="flex gap-2 mt-2">
                    <Button 
                      onClick={async () => {
                        if (!window.confirm('AI zoptymalizuje SEO tego wpisu i zapisze zmiany na stronie. Kontynuować?')) return;
                        setAutoFixingAudit(auditDetail.url);
                        try {
                          const res = await axios.post(`${API}/wp/auto-fix-from-audit?url=${encodeURIComponent(auditDetail.url)}`);
                          if (res.data.saved) {
                            toast.success(`SEO naprawione i zapisane: ${res.data.post_title || auditDetail.url}`);
                          } else if (res.data.save_error) {
                            toast.warning(res.data.save_error);
                          }
                        } catch(e) { 
                          const msg = e.response?.data?.detail || 'Błąd naprawy';
                          toast.error(msg); 
                        }
                        finally { setAutoFixingAudit(null); }
                      }} 
                      disabled={!!autoFixingAudit} 
                      className="gap-2 bg-green-600 hover:bg-green-700"
                      data-testid="auto-fix-audit-tab"
                    >
                      {autoFixingAudit === auditDetail.url ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} 
                      Napraw automatycznie
                    </Button>
                    <span className="text-xs text-gray-500 self-center">AI poprawi SEO i zapisze na stronie</span>
                  </div>
                  <div className="space-y-3 mt-4">
                    {auditDetail.issues.map((issue, i) => (
                      <div key={i} className="p-4 rounded-xl border border-gray-100 bg-gray-50">
                        <div className="flex items-start gap-3">
                          {severityIcon(issue.severity)}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{issue.issue}</p>
                            <p className="text-xs text-gray-500 mt-1">{issue.fix}</p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => getAIFix(auditDetail.url, issue.issue)} disabled={aiFixLoading === issue.issue} className="gap-1 text-xs shrink-0" data-testid={`audit-ai-fix-${i}`}>
                            {aiFixLoading === issue.issue ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} AI Porada
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {aiFix && (
                    <div className="mt-4 p-4 rounded-xl bg-purple-50 border border-purple-200">
                      <h4 className="font-medium text-purple-700 mb-2 flex items-center gap-2"><Wand2 className="w-4 h-4" /> Porada AI</h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{aiFix}</div>
                    </div>
                  )}
                  <div className="mt-4 grid grid-cols-3 gap-3 text-center text-xs">
                    <div className="p-3 bg-gray-50 rounded-xl"><div className="font-bold text-lg">{auditDetail.stats?.word_count || 0}</div>Słów</div>
                    <div className="p-3 bg-gray-50 rounded-xl"><div className="font-bold text-lg">{auditDetail.stats?.images || 0}</div>Obrazów</div>
                    <div className="p-3 bg-gray-50 rounded-xl"><div className="font-bold text-lg">{auditDetail.stats?.internal_links || 0}</div>Linków wewn.</div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// File Browser Component
const FileBrowser = ({ connectionId, onFileSelect, currentFile }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState('/');
  const [expanded, setExpanded] = useState(new Set(['/']));

  const loadFiles = useCallback(async (dirPath = '/') => {
    if (!connectionId) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/files/list?connection_id=${connectionId}&path=${encodeURIComponent(dirPath)}`);
      setFiles(response.data);
      setPath(dirPath);
    } catch (error) {
      toast.error('Nie można załadować plików');
    } finally {
      setLoading(false);
    }
  }, [connectionId]);

  useEffect(() => {
    if (connectionId) loadFiles('/');
  }, [connectionId, loadFiles]);

  const handleClick = (item) => {
    if (item.is_dir) {
      const newExpanded = new Set(expanded);
      if (newExpanded.has(item.path)) {
        newExpanded.delete(item.path);
      } else {
        newExpanded.add(item.path);
        loadFiles(item.path);
      }
      setExpanded(newExpanded);
    } else {
      onFileSelect(item);
    }
  };

  const goUp = () => {
    const parent = path.split('/').slice(0, -1).join('/') || '/';
    loadFiles(parent);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Button variant="ghost" size="icon" onClick={() => loadFiles(path)} disabled={loading} className="h-9 w-9">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {path !== '/' && (
            <Button variant="ghost" size="sm" onClick={goUp} className="h-9">
              <ChevronLeft className="w-4 h-4 mr-1" /> Wstecz
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-400 font-mono truncate">{path}</p>
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="p-4 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="p-2">
            {files.map((item) => (
              <div
                key={item.path}
                onClick={() => handleClick(item)}
                className={`file-item-friendly ${currentFile?.path === item.path ? 'active' : ''}`}
                data-testid={`file-${item.name}`}
              >
                {item.is_dir ? (
                  <>
                    {expanded.has(item.path) ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <FolderOpen className="w-5 h-5 text-amber-500" />
                  </>
                ) : (
                  <>
                    <span className="w-4" />
                    {getFileIcon(item.name)}
                  </>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.name}</p>
                  {!item.is_dir && (
                    <p className="text-xs text-gray-400">{getFriendlyTypeName(item.name)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// AI Assistant Component
const AIAssistant = ({ code, language, onApplyCode }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  const suggestions = [
    { label: "Napraw błędy", action: "fix", icon: <Zap className="w-4 h-4" /> },
    { label: "Popraw wygląd", action: "generate", prompt: "Popraw wygląd i dodaj nowoczesne style CSS", icon: <Sparkles className="w-4 h-4" /> },
    { label: "Wyjaśnij kod", action: "analyze", icon: <Lightbulb className="w-4 h-4" /> },
    { label: "Napisz treść", action: "content", icon: <Edit3 className="w-4 h-4" /> },
  ];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (customPrompt, customAction) => {
    const prompt = customPrompt || input;
    const action = customAction || 'generate';
    if (!prompt.trim() && !code) return;

    const userMsg = { role: 'user', content: customPrompt ? `${customPrompt}` : prompt };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/generate`, {
        prompt: prompt,
        code: code,
        language: language,
        action: action
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.result }]);
    } catch (error) {
      toast.error('Błąd AI. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  const extractCode = (content) => {
    const match = content.match(/```[\w]*\n([\s\S]*?)```/);
    return match ? match[1] : null;
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-purple-50 to-pink-50">
      <div className="p-4 border-b border-purple-100 bg-white/80 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">Asystent AI</h3>
            <p className="text-xs text-gray-500">Pomogę Ci z kodem</p>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 p-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-100 flex items-center justify-center">
              <MessageSquare className="w-8 h-8 text-purple-500" />
            </div>
            <h4 className="font-semibold mb-2">W czym mogę pomóc?</h4>
            <p className="text-sm text-gray-500 mb-6">Wybierz opcję lub napisz własne pytanie</p>
            
            <div className="grid grid-cols-2 gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s.prompt || `Proszę o: ${s.label}`, s.action)}
                  disabled={!code && s.action !== 'content'}
                  className="p-3 rounded-xl bg-white border border-purple-100 hover:border-purple-300 hover:bg-purple-50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`ai-suggestion-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-purple-500">{s.icon}</span>
                    <span className="text-sm font-medium">{s.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={`chat-bubble ${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <div>
                    {msg.content.includes('```') ? (
                      <>
                        <p className="text-sm mb-3 whitespace-pre-wrap">
                          {msg.content.split('```')[0]}
                        </p>
                        <div className="bg-gray-900 text-gray-100 p-4 rounded-xl text-xs font-mono overflow-x-auto">
                          {extractCode(msg.content)}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => onApplyCode(extractCode(msg.content))}
                          className="mt-3 h-9 rounded-lg"
                          data-testid="apply-ai-code"
                        >
                          <Check className="w-4 h-4 mr-1" /> Użyj tego kodu
                        </Button>
                      </>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-purple-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Myślę...</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}
      </ScrollArea>

      <div className="p-4 bg-white border-t border-purple-100">
        <div className="flex gap-2">
          <Input
            data-testid="ai-chat-input"
            placeholder="Napisz czego potrzebujesz..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            className="h-11 rounded-xl"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="h-11 w-11 rounded-xl p-0"
            data-testid="ai-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Live Preview Component
const LivePreview = ({ code, filename }) => {
  const [viewMode, setViewMode] = useState('desktop');
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current) return;
    
    const ext = filename?.split('.').pop().toLowerCase();
    let content = code;
    
    if (ext === 'css') {
      content = `<!DOCTYPE html>
<html><head><style>${code}</style></head>
<body style="padding: 20px; font-family: sans-serif;">
  <h1>Podgląd stylów CSS</h1>
  <p>Dodaj element HTML, żeby zobaczyć style.</p>
  <button>Przykładowy przycisk</button>
  <div class="test">Element testowy</div>
</body></html>`;
    } else if (ext === 'html' || ext === 'htm') {
      content = code;
    } else if (ext === 'php') {
      content = `<!DOCTYPE html>
<html><head><style>body{font-family:monospace;padding:20px;background:#f5f5f5;}</style></head>
<body>
  <h3>Podgląd PHP</h3>
  <p style="color:#666;">PHP wymaga serwera do uruchomienia. Poniżej widoczny jest kod źródłowy.</p>
  <pre style="background:white;padding:15px;border-radius:8px;overflow:auto;">${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
</body></html>`;
    } else {
      content = `<!DOCTYPE html>
<html><head></head><body style="padding:20px;font-family:sans-serif;">
<h3>Podgląd pliku</h3>
<pre style="background:#f5f5f5;padding:15px;border-radius:8px;overflow:auto;">${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
</body></html>`;
    }
    
    // Use srcdoc for reliable preview
    iframeRef.current.srcdoc = content;
  }, [code, filename]);

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium">Podgląd na żywo</span>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('desktop')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
            data-testid="preview-desktop"
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('mobile')}
            className={`p-2 rounded-md transition-colors ${viewMode === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
            data-testid="preview-mobile"
          >
            <Smartphone className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 p-4 bg-gray-50 overflow-auto flex justify-center">
        <div className={`bg-white rounded-lg shadow-lg overflow-hidden transition-all ${viewMode === 'mobile' ? 'w-[375px]' : 'w-full'}`}>
          <iframe
            ref={iframeRef}
            className="w-full h-full min-h-[500px]"
            title="Live Preview"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
};

// SEO Tool Component - Przyjazne narzędzie SEO dla laików z instrukcjami krok po kroku
const SEOTool = ({ code, filename, onUpdateCode }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [generatingMeta, setGeneratingMeta] = useState(false);
  const [generatedMeta, setGeneratedMeta] = useState(null);
  const [keywords, setKeywords] = useState('');
  const [improvementTips, setImprovementTips] = useState(null);
  const [loadingTips, setLoadingTips] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [fixingIssue, setFixingIssue] = useState(null);

  const analyzeCode = async () => {
    if (!code) return;
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/seo/analyze`, {
        html_content: code
      });
      setAnalysis(response.data);
    } catch (error) {
      toast.error('Błąd analizy SEO');
    } finally {
      setAnalyzing(false);
    }
  };

  const generateMeta = async () => {
    if (!code) return;
    setGeneratingMeta(true);
    try {
      const response = await axios.post(`${API}/seo/generate-meta`, {
        page_content: code,
        keywords: keywords
      });
      if (response.data.success) {
        setGeneratedMeta(response.data.meta);
        toast.success('Meta tagi wygenerowane!');
      } else {
        toast.error('Nie udało się wygenerować');
      }
    } catch (error) {
      toast.error('Błąd generowania');
    } finally {
      setGeneratingMeta(false);
    }
  };

  const getImprovementTips = async () => {
    if (!code) return;
    setLoadingTips(true);
    try {
      const response = await axios.post(`${API}/seo/improve-content`, {
        page_content: code,
        keywords: keywords
      });
      if (response.data.success) {
        setImprovementTips(response.data.suggestions);
      }
    } catch (error) {
      toast.error('Błąd pobierania porad');
    } finally {
      setLoadingTips(false);
    }
  };

  // Funkcje do automatycznego naprawiania problemów
  const fixMissingTitle = () => {
    if (!code) return;
    setFixingIssue('title');
    
    let newCode = code;
    const headMatch = code.match(/<head[^>]*>/i);
    
    if (headMatch) {
      // Usuń stary title jeśli istnieje
      newCode = newCode.replace(/<title[^>]*>.*?<\/title>/gi, '');
      // Dodaj nowy title
      const newTitle = generatedMeta?.title || 'Tytuł Twojej strony - zmień na własny';
      newCode = newCode.replace(/<head[^>]*>/i, `${headMatch[0]}\n    <title>${newTitle}</title>`);
      onUpdateCode(newCode);
      toast.success('✅ Tytuł dodany! Kliknij "Zapisz" aby zachować zmiany.');
    } else {
      toast.error('Nie znaleziono sekcji <head>');
    }
    setFixingIssue(null);
  };

  const fixMissingDescription = () => {
    if (!code) return;
    setFixingIssue('description');
    
    let newCode = code;
    const headMatch = code.match(/<head[^>]*>/i);
    
    if (headMatch) {
      // Usuń stary opis jeśli istnieje
      newCode = newCode.replace(/<meta[^>]*name=["']description["'][^>]*>/gi, '');
      // Dodaj nowy opis
      const newDesc = generatedMeta?.description || 'Opis Twojej strony - zachęć odwiedzających do kliknięcia. Napisz 150-160 znaków.';
      newCode = newCode.replace(/<head[^>]*>/i, `${headMatch[0]}\n    <meta name="description" content="${newDesc}">`);
      onUpdateCode(newCode);
      toast.success('✅ Opis dodany! Kliknij "Zapisz" aby zachować zmiany.');
    } else {
      toast.error('Nie znaleziono sekcji <head>');
    }
    setFixingIssue(null);
  };

  const fixMissingH1 = () => {
    if (!code) return;
    setFixingIssue('h1');
    
    let newCode = code;
    const bodyMatch = code.match(/<body[^>]*>/i);
    
    if (bodyMatch) {
      // Dodaj H1 na początku body
      newCode = newCode.replace(/<body[^>]*>/i, `${bodyMatch[0]}\n    <h1>Główny tytuł strony - zmień na własny</h1>`);
      onUpdateCode(newCode);
      toast.success('✅ Nagłówek H1 dodany! Zmień tekst na własny i kliknij "Zapisz".');
    } else {
      toast.error('Nie znaleziono sekcji <body>');
    }
    setFixingIssue(null);
  };

  const applyAllMetaTags = () => {
    if (!generatedMeta || !code) return;
    setFixingIssue('all');
    
    let newCode = code;
    const headMatch = code.match(/<head[^>]*>/i);
    
    if (headMatch) {
      const metaCode = `
    <title>${generatedMeta.title}</title>
    <meta name="description" content="${generatedMeta.description}">
    <meta name="keywords" content="${generatedMeta.keywords}">`;
      
      // Usuń stare meta tagi
      newCode = newCode.replace(/<title[^>]*>.*?<\/title>/gi, '');
      newCode = newCode.replace(/<meta[^>]*name=["']description["'][^>]*>/gi, '');
      newCode = newCode.replace(/<meta[^>]*name=["']keywords["'][^>]*>/gi, '');
      
      // Dodaj nowe
      newCode = newCode.replace(/<head[^>]*>/i, `${headMatch[0]}${metaCode}`);
      
      onUpdateCode(newCode);
      toast.success('✅ Wszystkie meta tagi dodane! Kliknij "Zapisz" aby zachować.');
    } else {
      toast.error('Nie znaleziono sekcji <head> w kodzie');
    }
    setFixingIssue(null);
  };

  // NOWA FUNKCJA: Napraw wszystko jednym kliknięciem
  const fixAllIssues = async () => {
    if (!code || !analysis?.issues) return;
    setFixingIssue('all-issues');
    
    let newCode = code;
    let fixedCount = 0;
    const fixedItems = [];
    
    // Najpierw wygeneruj meta tagi AI jeśli ich nie ma
    let metaToUse = generatedMeta;
    if (!metaToUse) {
      try {
        const response = await axios.post(`${API}/seo/generate-meta`, {
          page_content: code,
          keywords: keywords
        });
        if (response.data.success) {
          metaToUse = response.data.meta;
          setGeneratedMeta(metaToUse);
        }
      } catch (error) {
        console.error('Error generating meta:', error);
      }
    }
    
    const headMatch = newCode.match(/<head[^>]*>/i);
    const bodyMatch = newCode.match(/<body[^>]*>/i);
    
    // Sprawdź każdy problem i napraw
    for (const issue of analysis.issues) {
      // Brak tytułu lub tytuł za krótki
      if ((issue.title.includes('Brak tytułu') || issue.title.includes('Tytuł strony za krótki')) && headMatch) {
        newCode = newCode.replace(/<title[^>]*>.*?<\/title>/gi, '');
        const newTitle = metaToUse?.title || 'Tytuł Twojej strony - zmień na własny';
        newCode = newCode.replace(/<head[^>]*>/i, `${headMatch[0]}\n    <title>${newTitle}</title>`);
        fixedCount++;
        fixedItems.push('✅ Tytuł strony');
      }
      
      // Brak opisu lub opis za krótki
      if ((issue.title.includes('Brak opisu') || issue.title.includes('Opis strony za krótki')) && headMatch) {
        newCode = newCode.replace(/<meta[^>]*name=["']description["'][^>]*>/gi, '');
        const newDesc = metaToUse?.description || 'Opis Twojej strony - zachęć odwiedzających do kliknięcia.';
        newCode = newCode.replace(/<head[^>]*>/i, `${newCode.match(/<head[^>]*>/i)[0]}\n    <meta name="description" content="${newDesc}">`);
        fixedCount++;
        fixedItems.push('✅ Opis strony');
      }
      
      // Brak H1
      if (issue.title.includes('Brak głównego nagłówka') && bodyMatch) {
        const h1Text = metaToUse?.title?.split(' - ')[0] || 'Główny tytuł strony';
        newCode = newCode.replace(/<body[^>]*>/i, `${newCode.match(/<body[^>]*>/i)[0]}\n    <h1>${h1Text}</h1>`);
        fixedCount++;
        fixedItems.push('✅ Nagłówek H1');
      }
    }
    
    // Dodaj słowa kluczowe jeśli mamy meta
    if (metaToUse?.keywords && headMatch) {
      newCode = newCode.replace(/<meta[^>]*name=["']keywords["'][^>]*>/gi, '');
      newCode = newCode.replace(/<head[^>]*>/i, `${newCode.match(/<head[^>]*>/i)[0]}\n    <meta name="keywords" content="${metaToUse.keywords}">`);
    }
    
    if (fixedCount > 0) {
      onUpdateCode(newCode);
      toast.success(
        <div>
          <p className="font-semibold">🎉 Naprawiono {fixedCount} problemów!</p>
          <ul className="text-sm mt-1">
            {fixedItems.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="text-xs mt-2 opacity-80">Kliknij "Zapisz" aby zachować zmiany</p>
        </div>,
        { duration: 5000 }
      );
      // Odśwież analizę po naprawie
      setTimeout(() => analyzeCode(), 1000);
    } else {
      toast.info('Nie znaleziono problemów do automatycznej naprawy');
    }
    
    setFixingIssue(null);
  };

  // Policz ile problemów można naprawić automatycznie
  const countFixableIssues = () => {
    if (!analysis?.issues) return 0;
    return analysis.issues.filter(issue => 
      issue.title.includes('Brak tytułu') ||
      issue.title.includes('Tytuł strony za krótki') ||
      issue.title.includes('Brak opisu') ||
      issue.title.includes('Opis strony za krótki') ||
      issue.title.includes('Brak głównego nagłówka')
    ).length;
  };

  // Szczegółowe instrukcje dla każdego typu problemu
  const getDetailedInstructions = (issue) => {
    const instructions = {
      'Brak tytułu strony!': {
        what: 'Tytuł strony (title) to tekst, który wyświetla się w zakładce przeglądarki i w wynikach Google jako niebieski link.',
        why: 'Google używa tytułu, żeby zrozumieć o czym jest strona. Dobry tytuł przyciąga kliknięcia.',
        where: 'Tytuł dodaje się w kodzie strony, wewnątrz sekcji <head>...</head>',
        steps: [
          'Znajdź w kodzie linię z <head>',
          'Zaraz pod nią dodaj: <title>Twój tytuł strony</title>',
          'Tytuł powinien mieć 50-60 znaków',
          'Użyj słów, których ludzie szukają'
        ],
        example: '<title>Firma Budowlana Kurdynowski - Budowa i remonty Warszawa</title>',
        canAutoFix: true,
        fixFunction: fixMissingTitle
      },
      'Brak opisu strony!': {
        what: 'Opis strony (meta description) to tekst, który wyświetla się w Google pod tytułem strony.',
        why: 'Dobry opis zachęca ludzi do kliknięcia w Twoją stronę zamiast konkurencji.',
        where: 'Opis dodaje się w kodzie strony, wewnątrz sekcji <head>...</head>',
        steps: [
          'Znajdź w kodzie linię z <head>',
          'Dodaj linię: <meta name="description" content="Twój opis">',
          'Opis powinien mieć 150-160 znaków',
          'Napisz co oferujesz i dlaczego warto wybrać Ciebie'
        ],
        example: '<meta name="description" content="Firma budowlana z 20-letnim doświadczeniem. Budowa domów, remonty i wykończenia. Bezpłatna wycena!">',
        canAutoFix: true,
        fixFunction: fixMissingDescription
      },
      'Brak głównego nagłówka (H1)': {
        what: 'Nagłówek H1 to główny tytuł widoczny na stronie (nie mylić z tytułem w zakładce).',
        why: 'Google traktuje H1 jako najważniejszy tekst na stronie. Pomaga zrozumieć temat.',
        where: 'H1 dodaje się w kodzie strony, wewnątrz sekcji <body>...</body>, najlepiej na początku.',
        steps: [
          'Znajdź w kodzie linię z <body>',
          'Zaraz pod nią dodaj: <h1>Twój główny tytuł</h1>',
          'Użyj tylko jednego H1 na stronie',
          'H1 powinien opisywać główny temat strony'
        ],
        example: '<h1>Usługi budowlane i remontowe w Warszawie</h1>',
        canAutoFix: true,
        fixFunction: fixMissingH1
      },
      'Za dużo głównych nagłówków (H1)': {
        what: 'Strona ma więcej niż jeden nagłówek H1.',
        why: 'Google może się pogubić, który temat jest najważniejszy.',
        where: 'Znajdź wszystkie <h1> w kodzie i zostaw tylko jeden.',
        steps: [
          'Wyszukaj w kodzie wszystkie <h1>',
          'Wybierz jeden najważniejszy - zostaw go jako H1',
          'Pozostałe zmień na <h2> (podtytuły)',
          'Zapisz zmiany'
        ],
        example: 'Zmień <h1>Drugi tytuł</h1> na <h2>Drugi tytuł</h2>',
        canAutoFix: false
      },
      'Tytuł strony za krótki': {
        what: 'Twój tytuł ma za mało znaków.',
        why: 'Krótki tytuł nie wykorzystuje miejsca w Google i może być mniej atrakcyjny.',
        where: 'Znajdź <title>...</title> w sekcji <head>',
        steps: [
          'Znajdź w kodzie linię z <title>',
          'Rozwiń tytuł do 50-60 znaków',
          'Dodaj słowa kluczowe, lokalizację lub zachętę',
          'Zapisz zmiany'
        ],
        example: 'Zamiast "Firma Budowlana" napisz "Firma Budowlana Kurdynowski - Budowa domów i remonty Warszawa"',
        canAutoFix: true,
        fixFunction: fixMissingTitle
      },
      'Tytuł strony za długi': {
        what: 'Twój tytuł ma za dużo znaków.',
        why: 'Google skróci tytuł i doda "..." - użytkownik nie zobaczy całości.',
        where: 'Znajdź <title>...</title> w sekcji <head>',
        steps: [
          'Znajdź w kodzie linię z <title>',
          'Skróć tytuł do maksymalnie 60 znaków',
          'Zostaw najważniejsze słowa na początku',
          'Zapisz zmiany'
        ],
        example: 'Skróć do najważniejszych informacji',
        canAutoFix: false
      },
      'Opis strony za krótki': {
        what: 'Twój opis meta ma za mało znaków.',
        why: 'Krótki opis nie wykorzystuje miejsca w Google i może być mniej przekonujący.',
        where: 'Znajdź <meta name="description"...> w sekcji <head>',
        steps: [
          'Znajdź w kodzie linię z meta description',
          'Rozwiń opis do 150-160 znaków',
          'Dodaj korzyści i zachętę do działania',
          'Zapisz zmiany'
        ],
        example: 'Dodaj więcej szczegółów o ofercie i korzyściach',
        canAutoFix: true,
        fixFunction: fixMissingDescription
      },
      'Opis strony za długi': {
        what: 'Twój opis meta ma za dużo znaków.',
        why: 'Google skróci opis - ważne informacje mogą zostać ucięte.',
        where: 'Znajdź <meta name="description"...> w sekcji <head>',
        steps: [
          'Znajdź w kodzie linię z meta description',
          'Skróć opis do 150-160 znaków',
          'Zostaw najważniejsze informacje na początku',
          'Zapisz zmiany'
        ],
        example: 'Skróć zachowując kluczowe informacje',
        canAutoFix: false
      }
    };
    
    // Obrazki bez alt
    if (issue.title.includes('Obrazki bez opisów')) {
      return {
        what: 'Obrazki na stronie nie mają opisów tekstowych (atrybut alt).',
        why: 'Google nie "widzi" obrazków - czyta tylko ich opisy. Bez alt obrazki są niewidoczne dla wyszukiwarek.',
        where: 'Znajdź tagi <img> w kodzie strony.',
        steps: [
          'Znajdź wszystkie tagi <img src="...">',
          'Do każdego dodaj atrybut alt="opis"',
          'Opis powinien mówić co jest na obrazku',
          'Użyj słów kluczowych jeśli pasują'
        ],
        example: '<img src="dom.jpg" alt="Nowy dom jednorodzinny wybudowany przez naszą firmę">',
        canAutoFix: false
      };
    }
    
    // Podejrzane linki
    if (issue.title.includes('Podejrzane linki')) {
      return {
        what: 'Na stronie są linki do podejrzanych stron (spam, kasyna, itp.).',
        why: 'Google może ukarać Twoją stronę za linkowanie do spam\'u - spadniesz w wynikach!',
        where: 'Znajdź tagi <a href="..."> w kodzie strony.',
        steps: [
          'Przejrzyj wszystkie linki na stronie',
          'Usuń podejrzane linki <a href="...">...</a>',
          'Sprawdź czy nie zostałaś zhakowana',
          'Zmień hasła do panelu WordPress'
        ],
        example: 'Usuń całą linię: <a href="http://spam-site.com">...</a>',
        canAutoFix: false
      };
    }
    
    return instructions[issue.title] || {
      what: issue.description,
      why: 'To wpływa na pozycję strony w Google.',
      where: 'W kodzie HTML strony.',
      steps: [issue.suggestion],
      example: issue.element || '',
      canAutoFix: false
    };
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getIssueIcon = (type) => {
    switch (type) {
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'meta': return <Tag className="w-4 h-4" />;
      case 'links': return <Link className="w-4 h-4" />;
      case 'headings': return <Type className="w-4 h-4" />;
      case 'images': return <Image className="w-4 h-4" />;
      default: return <FileSearch className="w-4 h-4" />;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-emerald-50 to-teal-50">
      {/* Header */}
      <div className="p-4 bg-white/80 backdrop-blur border-b border-emerald-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold">Narzędzia SEO</h3>
            <p className="text-xs text-gray-500">Popraw widoczność w Google</p>
          </div>
        </div>
        <Button
          onClick={analyzeCode}
          disabled={analyzing || !code}
          className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700"
          data-testid="seo-analyze-btn"
        >
          {analyzing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizuję...</>
          ) : (
            <><Search className="w-4 h-4 mr-2" /> Sprawdź stronę</>
          )}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-emerald-100 bg-white/50">
        {[
          { id: 'overview', label: 'Wyniki', icon: <Target className="w-4 h-4" /> },
          { id: 'meta', label: 'Opisy', icon: <Tag className="w-4 h-4" /> },
          { id: 'tips', label: 'Porady', icon: <Lightbulb className="w-4 h-4" /> },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 px-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors
              ${activeTab === tab.id ? 'text-emerald-700 border-b-2 border-emerald-600 bg-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 p-4">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {!analysis ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-100 flex items-center justify-center">
                  <Search className="w-8 h-8 text-emerald-500" />
                </div>
                <h4 className="font-semibold mb-2">Sprawdź swoją stronę</h4>
                <p className="text-sm text-gray-500">
                  Kliknij "Sprawdź stronę" żeby zobaczyć<br />co można poprawić dla Google
                </p>
              </div>
            ) : (
              <>
                {/* Score */}
                <div className={`p-6 rounded-2xl ${getScoreBg(analysis.score)} text-center`}>
                  <p className="text-sm font-medium text-gray-600 mb-1">Ocena SEO</p>
                  <p className={`text-5xl font-bold ${getScoreColor(analysis.score)}`}>
                    {analysis.score}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">na 100 punktów</p>
                </div>

                {/* Przycisk NAPRAW WSZYSTKO */}
                {countFixableIssues() > 0 && (
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Wand2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg">Napraw wszystko automatycznie!</h4>
                        <p className="text-sm text-white/80">
                          Mogę naprawić {countFixableIssues()} {countFixableIssues() === 1 ? 'problem' : 
                            countFixableIssues() < 5 ? 'problemy' : 'problemów'} jednym kliknięciem
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={fixAllIssues}
                      disabled={fixingIssue !== null}
                      className="w-full h-14 rounded-xl bg-white text-emerald-700 hover:bg-white/90 font-bold text-base shadow-md"
                      data-testid="fix-all-btn"
                    >
                      {fixingIssue === 'all-issues' ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Naprawiam wszystko...</>
                      ) : (
                        <><Sparkles className="w-5 h-5 mr-2" /> Napraw wszystko jednym kliknięciem</>
                      )}
                    </Button>
                    <p className="text-xs text-center text-white/70 mt-2">
                      AI wygeneruje i doda brakujące tytuły, opisy i nagłówki
                    </p>
                  </div>
                )}

                {/* Issues */}
                {analysis.issues.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      Do poprawy ({analysis.issues.length})
                    </h4>
                    {analysis.issues.map((issue, i) => {
                      const instructions = getDetailedInstructions(issue);
                      const isExpanded = expandedIssue === i;
                      
                      return (
                        <div key={i} className="rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                          {/* Nagłówek problemu */}
                          <div 
                            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => setExpandedIssue(isExpanded ? null : i)}
                          >
                            <div className="flex items-start gap-3">
                              {getIssueIcon(issue.type)}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 flex items-center gap-1">
                                    {getCategoryIcon(issue.category)}
                                    {issue.category === 'meta' ? 'Opisy' : 
                                     issue.category === 'links' ? 'Linki' :
                                     issue.category === 'headings' ? 'Nagłówki' :
                                     issue.category === 'images' ? 'Obrazki' : issue.category}
                                  </span>
                                </div>
                                <h5 className="font-medium text-sm">{issue.title}</h5>
                                <p className="text-xs text-gray-500 mt-1">{issue.description}</p>
                              </div>
                              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                          
                          {/* Rozwinięte instrukcje */}
                          {isExpanded && (
                            <div className="border-t border-gray-100 p-4 bg-gradient-to-b from-gray-50 to-white animate-fadeIn">
                              {/* Co to jest */}
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">?</span>
                                  <span className="font-medium text-sm">Co to jest?</span>
                                </div>
                                <p className="text-sm text-gray-600 ml-8">{instructions.what}</p>
                              </div>
                              
                              {/* Dlaczego ważne */}
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 flex items-center justify-center text-xs font-bold">!</span>
                                  <span className="font-medium text-sm">Dlaczego to ważne?</span>
                                </div>
                                <p className="text-sm text-gray-600 ml-8">{instructions.why}</p>
                              </div>
                              
                              {/* Gdzie szukać */}
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">📍</span>
                                  <span className="font-medium text-sm">Gdzie to jest w kodzie?</span>
                                </div>
                                <p className="text-sm text-gray-600 ml-8">{instructions.where}</p>
                              </div>
                              
                              {/* Kroki do wykonania */}
                              <div className="mb-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">✓</span>
                                  <span className="font-medium text-sm">Co zrobić krok po kroku:</span>
                                </div>
                                <div className="ml-8 space-y-2">
                                  {instructions.steps.map((step, stepIndex) => (
                                    <div key={stepIndex} className="flex items-start gap-2">
                                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                        {stepIndex + 1}
                                      </span>
                                      <p className="text-sm text-gray-700">{step}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Przykład */}
                              {instructions.example && (
                                <div className="mb-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">📝</span>
                                    <span className="font-medium text-sm">Przykład:</span>
                                  </div>
                                  <div className="ml-8 p-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto">
                                    {instructions.example}
                                  </div>
                                </div>
                              )}
                              
                              {/* Przycisk napraw automatycznie */}
                              {instructions.canAutoFix && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <Button
                                    onClick={(e) => { e.stopPropagation(); instructions.fixFunction(); }}
                                    disabled={fixingIssue !== null}
                                    className="w-full h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700"
                                    data-testid={`fix-issue-${i}`}
                                  >
                                    {fixingIssue ? (
                                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Naprawiam...</>
                                    ) : (
                                      <><Wand2 className="w-4 h-4 mr-2" /> Napraw to za mnie automatycznie</>
                                    )}
                                  </Button>
                                  <p className="text-xs text-center text-gray-500 mt-2">
                                    Kliknij, a kod zostanie poprawiony automatycznie
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-6 rounded-xl bg-green-50 text-center">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <h4 className="font-semibold text-green-700">Świetnie!</h4>
                    <p className="text-sm text-green-600">Nie znaleziono poważnych problemów</p>
                  </div>
                )}

                {/* Links summary */}
                {analysis.meta?.links && (
                  <div className="p-4 rounded-xl bg-white border border-gray-100">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Link className="w-4 h-4" /> Linki na stronie
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-3 rounded-lg bg-blue-50">
                        <p className="text-2xl font-bold text-blue-600">{analysis.meta.links.internal?.length || 0}</p>
                        <p className="text-xs text-gray-500">Wewnętrzne</p>
                      </div>
                      <div className="p-3 rounded-lg bg-purple-50">
                        <p className="text-2xl font-bold text-purple-600">{analysis.meta.links.external?.length || 0}</p>
                        <p className="text-xs text-gray-500">Zewnętrzne</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-50">
                        <p className="text-2xl font-bold text-red-600">{analysis.meta.links.suspicious?.length || 0}</p>
                        <p className="text-xs text-gray-500">Podejrzane</p>
                      </div>
                    </div>
                    {analysis.meta.links.suspicious?.length > 0 && (
                      <div className="mt-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                        ⚠️ <strong>Uwaga!</strong> Znaleziono podejrzane linki. Mogą to być spam lub szkodliwe linki obniżające pozycję w Google.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'meta' && (
          <div className="space-y-4">
            {/* Wyjaśnienie krok po kroku */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Tag className="w-5 h-5 text-blue-600" /> 
                Meta tagi - co to jest i jak to działa?
              </h4>
              
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <div>
                    <p className="font-medium text-gray-800">Tytuł strony (title)</p>
                    <p className="text-gray-600 text-xs mt-1">To niebieski link w Google. Ludzie klikają właśnie w tytuł!</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <div>
                    <p className="font-medium text-gray-800">Opis strony (description)</p>
                    <p className="text-gray-600 text-xs mt-1">Tekst pod tytułem w Google. Przekonuje, żeby kliknąć właśnie w Ciebie.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <div>
                    <p className="font-medium text-gray-800">Słowa kluczowe</p>
                    <p className="text-gray-600 text-xs mt-1">Słowa, po których ludzie szukają Twojej strony w Google.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Generator */}
            <div className="p-4 rounded-xl bg-white border border-gray-100">
              <h4 className="font-semibold mb-3">Wygeneruj meta tagi automatycznie</h4>
              <p className="text-sm text-gray-500 mb-4">
                AI przeanalizuje Twoją stronę i napisze za Ciebie tytuł i opis.
              </p>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    Słowa kluczowe (opcjonalnie)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-4 h-4 inline ml-1 text-gray-400" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Wpisz słowa, po których ludzie szukają Twojej strony.</p>
                          <p className="mt-1 text-xs opacity-80">Np. "usługi budowlane Warszawa" albo "remont mieszkania"</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Input
                    placeholder="np. usługi budowlane, remont, Warszawa"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    className="h-11 rounded-xl"
                  />
                </div>
                <Button
                  onClick={generateMeta}
                  disabled={generatingMeta || !code}
                  className="w-full h-12 rounded-xl text-base"
                  data-testid="generate-meta-btn"
                >
                  {generatingMeta ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> AI pisze opisy...</>
                  ) : (
                    <><Wand2 className="w-5 h-5 mr-2" /> Wygeneruj opisy</>
                  )}
                </Button>
              </div>
            </div>

            {/* Wygenerowane meta tagi */}
            {generatedMeta && (
              <div className="space-y-4 animate-fadeIn">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-emerald-700">Gotowe! Oto Twoje nowe opisy:</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-white">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-500">Tytuł strony</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${generatedMeta.title?.length <= 60 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {generatedMeta.title?.length}/60 znaków
                        </span>
                      </div>
                      <p className="font-medium text-blue-600">{generatedMeta.title}</p>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-white">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-500">Opis strony</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${generatedMeta.description?.length <= 160 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {generatedMeta.description?.length}/160 znaków
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{generatedMeta.description}</p>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-white">
                      <p className="text-xs text-gray-500 mb-1">Słowa kluczowe</p>
                      <p className="text-sm text-gray-700">{generatedMeta.keywords}</p>
                    </div>
                  </div>
                </div>
                
                {/* Co teraz zrobić */}
                <div className="p-4 rounded-xl bg-white border border-gray-100">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-emerald-600" />
                    Co teraz zrobić?
                  </h4>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                      <p className="text-sm text-gray-700">Kliknij przycisk poniżej - AI doda opisy do Twojego kodu</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                      <p className="text-sm text-gray-700">Kliknij "Zapisz" w górnym pasku, żeby zachować zmiany</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                      <p className="text-sm text-gray-700">Google zaktualizuje opisy w ciągu kilku dni</p>
                    </div>
                  </div>
                  
                  <Button
                    onClick={applyAllMetaTags}
                    disabled={fixingIssue !== null}
                    className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-base"
                    data-testid="apply-meta-btn"
                  >
                    {fixingIssue ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Dodaję...</>
                    ) : (
                      <><Check className="w-5 h-5 mr-2" /> Dodaj te opisy do mojego kodu</>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Preview in Google */}
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
              <p className="text-xs text-gray-500 mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Podgląd - tak Twoja strona będzie wyglądać w Google:
              </p>
              <div className="p-4 bg-white rounded-lg shadow-sm">
                <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                  {generatedMeta?.title || analysis?.meta?.title || 'Tutaj pojawi się tytuł Twojej strony'}
                </p>
                <p className="text-green-700 text-sm">www.twojadomena.pl</p>
                <p className="text-sm text-gray-600 mt-1">
                  {generatedMeta?.description || analysis?.meta?.description || 'Tutaj pojawi się opis Twojej strony, który zachęci ludzi do kliknięcia...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-white border border-gray-100">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-yellow-500" />
                Personalne porady AI
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                AI przeanalizuje Twoją stronę i powie co konkretnie poprawić.
              </p>
              <Input
                placeholder="Słowa kluczowe (opcjonalnie)"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="h-11 rounded-xl mb-3"
              />
              <Button
                onClick={getImprovementTips}
                disabled={loadingTips || !code}
                className="w-full h-11 rounded-xl"
                data-testid="get-tips-btn"
              >
                {loadingTips ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizuję...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Daj mi porady</>
                )}
              </Button>
            </div>

            {improvementTips && (
              <div className="p-4 rounded-xl bg-white border border-gray-100 animate-fadeIn">
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {improvementTips}
                </div>
              </div>
            )}

            {!improvementTips && !loadingTips && (
              <div className="space-y-3">
                <h4 className="font-semibold">Podstawowe wskazówki SEO:</h4>
                {[
                  { icon: '📝', title: 'Unikalne tytuły', desc: 'Każda strona powinna mieć inny tytuł' },
                  { icon: '📖', title: 'Dobry opis', desc: 'Opis 150-160 znaków zachęcający do kliknięcia' },
                  { icon: '🔤', title: 'Jeden nagłówek H1', desc: 'Każda strona powinna mieć jeden główny tytuł' },
                  { icon: '🖼️', title: 'Opisy obrazków', desc: 'Dodaj tekst alternatywny (alt) do każdego obrazka' },
                  { icon: '🔗', title: 'Sprawdzaj linki', desc: 'Usuń podejrzane i niedziałające linki' },
                ].map((tip, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white border border-gray-100 flex items-start gap-3">
                    <span className="text-2xl">{tip.icon}</span>
                    <div>
                      <p className="font-medium text-sm">{tip.title}</p>
                      <p className="text-xs text-gray-500">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// Main Editor Component  
const Editor = ({ connection, onBack, user, onLogout }) => {
  const [currentFile, setCurrentFile] = useState(null);
  const [code, setCode] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [showAI, setShowAI] = useState(false);
  const [showSEO, setShowSEO] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // 'ai' | 'seo' | null
  const isDemo = connection.isDemo;

  // Demo files
  const demoFiles = [
    { name: 'index.html', path: '/index.html', is_dir: false },
    { name: 'style.css', path: '/style.css', is_dir: false },
    { name: 'kontakt.html', path: '/kontakt.html', is_dir: false },
  ];

  const demoContents = {
    '/index.html': `<!DOCTYPE html>
<html lang="pl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="stylesheet" href="style.css">
</head>
<body>
    <header>
        <h1>Firma Budowlana Kurdynowski</h1>
        <nav>
            <a href="index.html">Strona główna</a>
            <a href="kontakt.html">Kontakt</a>
        </nav>
    </header>
    
    <main>
        <section class="hero">
            <h2>Budujemy Twoje marzenia</h2>
            <p>Profesjonalne usługi budowlane od 20 lat</p>
        </section>
        
        <section class="uslugi">
            <h2>Nasze usługi</h2>
            <div class="cards">
                <div class="card">
                    <h3>Budowa domów</h3>
                    <p>Kompleksowa budowa domów jednorodzinnych</p>
                </div>
                <div class="card">
                    <h3>Remonty</h3>
                    <p>Remonty mieszkań i domów</p>
                </div>
                <div class="card">
                    <h3>Wykończenia</h3>
                    <p>Wykończenia wnętrz pod klucz</p>
                </div>
            </div>
        </section>
    </main>
    
    <footer>
        <p>&copy; 2024 Firma Budowlana Kurdynowski</p>
    </footer>
</body>
</html>`,
    '/style.css': `/* Style dla strony Firma Budowlana */

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: #333;
}

header {
    background: #2c3e50;
    color: white;
    padding: 1rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

header h1 {
    font-size: 1.5rem;
}

nav a {
    color: white;
    text-decoration: none;
    margin-left: 1.5rem;
}

nav a:hover {
    text-decoration: underline;
}

.hero {
    background: linear-gradient(135deg, #3498db, #2c3e50);
    color: white;
    padding: 4rem 2rem;
    text-align: center;
}

.hero h2 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
}

.uslugi {
    padding: 3rem 2rem;
    background: #f9f9f9;
}

.uslugi h2 {
    text-align: center;
    margin-bottom: 2rem;
}

.cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    max-width: 1000px;
    margin: 0 auto;
}

.card {
    background: white;
    padding: 1.5rem;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.card h3 {
    color: #3498db;
    margin-bottom: 0.5rem;
}

footer {
    background: #2c3e50;
    color: white;
    text-align: center;
    padding: 1.5rem;
}`,
    '/kontakt.html': `<!DOCTYPE html>
<html lang="pl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="stylesheet" href="style.css">
</head>
<body>
    <header>
        <h1>Firma Budowlana Kurdynowski</h1>
        <nav>
            <a href="index.html">Strona główna</a>
            <a href="kontakt.html">Kontakt</a>
        </nav>
    </header>
    
    <main>
        <section style="padding: 3rem 2rem; max-width: 600px; margin: 0 auto;">
            <h2>Kontakt</h2>
            <p style="margin: 1rem 0;">Skontaktuj się z nami:</p>
            
            <div style="margin: 2rem 0;">
                <p><strong>Telefon:</strong> +48 123 456 789</p>
                <p><strong>Email:</strong> kontakt@kurdynowski.com.pl</p>
                <p><strong>Adres:</strong> ul. Przykładowa 1, Warszawa</p>
            </div>
            
            <form>
                <div style="margin-bottom: 1rem;">
                    <label>Imię i nazwisko:</label><br>
                    <input type="text" style="width: 100%; padding: 0.5rem; margin-top: 0.25rem;">
                </div>
                <div style="margin-bottom: 1rem;">
                    <label>Email:</label><br>
                    <input type="email" style="width: 100%; padding: 0.5rem; margin-top: 0.25rem;">
                </div>
                <div style="margin-bottom: 1rem;">
                    <label>Wiadomość:</label><br>
                    <textarea rows="4" style="width: 100%; padding: 0.5rem; margin-top: 0.25rem;"></textarea>
                </div>
                <button type="submit" style="background: #3498db; color: white; padding: 0.75rem 1.5rem; border: none; cursor: pointer;">
                    Wyślij
                </button>
            </form>
        </section>
    </main>
    
    <footer>
        <p>&copy; 2024 Firma Budowlana Kurdynowski</p>
    </footer>
</body>
</html>`
  };

  const openFile = async (file) => {
    if (file.is_dir) return;
    setLoading(true);
    
    // Demo mode - use local content
    if (isDemo) {
      setCurrentFile(file);
      const content = demoContents[file.path] || '<!-- Pusty plik -->';
      setCode(content);
      setOriginalCode(content);
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/files/read`, {
        connection_id: connection.id,
        path: file.path
      });
      setCurrentFile(file);
      setCode(response.data.content);
      setOriginalCode(response.data.content);
    } catch (error) {
      toast.error('Nie można otworzyć pliku');
    } finally {
      setLoading(false);
    }
  };

  const saveFile = async () => {
    if (!currentFile) return;
    
    // Demo mode - just save locally
    if (isDemo) {
      setOriginalCode(code);
      toast.success('✨ Zapisano! (tryb demo - zmiany lokalne)');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/files/save`, {
        connection_id: connection.id,
        path: currentFile.path,
        content: code
      });
      setOriginalCode(code);
      toast.success('✨ Zapisano pomyślnie!');
    } catch (error) {
      toast.error('Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = code !== originalCode;

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Toolbar */}
      <div className="h-16 px-6 flex items-center justify-between bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="h-10 gap-2" data-testid="back-to-dashboard">
            <ChevronLeft className="w-4 h-4" /> Panel
          </Button>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-500" />
            <span className="font-semibold">{connection.name}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {currentFile && (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowPreview(!showPreview)}
                className={`h-10 gap-2 ${showPreview ? 'text-indigo-600' : ''}`}
                data-testid="toggle-preview"
              >
                {showPreview ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                Podgląd
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setActivePanel(activePanel === 'ai' ? null : 'ai'); }}
                className={`h-10 gap-2 ${activePanel === 'ai' ? 'text-purple-600 bg-purple-50' : ''}`}
                data-testid="toggle-ai"
              >
                <Wand2 className="w-4 h-4" /> AI
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setActivePanel(activePanel === 'seo' ? null : 'seo'); }}
                className={`h-10 gap-2 ${activePanel === 'seo' ? 'text-emerald-600 bg-emerald-50' : ''}`}
                data-testid="toggle-seo"
              >
                <TrendingUp className="w-4 h-4" /> SEO
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <Button
                variant="ghost"
                onClick={() => { setCode(originalCode); }}
                disabled={!hasChanges}
                className="h-10 gap-2"
                data-testid="undo-changes"
              >
                <Undo2 className="w-4 h-4" /> Cofnij
              </Button>
              <Button
                onClick={saveFile}
                disabled={saving || !hasChanges}
                className="h-10 gap-2 px-6 rounded-full"
                data-testid="save-file"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Zapisz {hasChanges && '*'}
              </Button>
            </>
          )}
          {user && (
            <div className="flex items-center gap-2 ml-2">
              <div className="h-6 w-px bg-gray-200" />
              <span className="text-xs text-gray-400 hidden md:inline">{user.split('@')[0]}</span>
              <Button variant="ghost" onClick={onLogout} className="h-10 gap-1 text-gray-500 hover:text-red-500" data-testid="logout-btn">
                <X className="w-4 h-4" /> Wyloguj
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* File Browser - Demo or Real */}
        <div className="w-64 border-r border-gray-200 flex-shrink-0">
          {isDemo ? (
            <div className="h-full flex flex-col bg-white">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-2 text-purple-600 mb-2">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm font-medium">Tryb demo</span>
                </div>
                <p className="text-xs text-gray-500">Przykładowe pliki do nauki</p>
              </div>
              <ScrollArea className="flex-1 p-2">
                {demoFiles.map((file) => (
                  <div
                    key={file.path}
                    onClick={() => openFile(file)}
                    className={`file-item-friendly ${currentFile?.path === file.path ? 'active' : ''}`}
                    data-testid={`demo-file-${file.name}`}
                  >
                    <span className="w-4" />
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{file.name}</p>
                      <p className="text-xs text-gray-400">{getFriendlyTypeName(file.name)}</p>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>
          ) : (
            <FileBrowser
              connectionId={connection.id}
              onFileSelect={openFile}
              currentFile={currentFile}
            />
          )}
        </div>

        {/* Editor Area */}
        <div className="flex-1 flex overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          ) : currentFile ? (
            <>
              {/* Code Editor */}
              <div className={`flex-1 min-w-0 ${showPreview ? 'border-r border-gray-200' : ''}`}>
                <div className="h-10 px-4 flex items-center gap-2 bg-white border-b border-gray-100">
                  {getFileIcon(currentFile.name)}
                  <span className="font-medium text-sm">{currentFile.name}</span>
                  {hasChanges && <span className="w-2 h-2 rounded-full bg-orange-400" />}
                </div>
                <CodeMirror
                  value={code}
                  height="calc(100% - 40px)"
                  extensions={[getLanguageExtension(currentFile.name)]}
                  onChange={(value) => setCode(value)}
                  data-testid="code-editor"
                />
              </div>

              {/* Live Preview */}
              {showPreview && (
                <div className="flex-1 min-w-0">
                  <LivePreview code={code} filename={currentFile.name} />
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-white">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-indigo-100 flex items-center justify-center">
                  <FileCode className="w-10 h-10 text-indigo-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Wybierz plik</h3>
                <p className="text-gray-500">Kliknij na plik po lewej stronie, żeby go otworzyć</p>
              </div>
            </div>
          )}
        </div>

        {/* AI Assistant */}
        {activePanel === 'ai' && currentFile && (
          <div className="w-80 border-l border-gray-200 flex-shrink-0">
            <AIAssistant
              code={code}
              language={currentFile.name.split('.').pop()}
              onApplyCode={(newCode) => {
                setCode(newCode);
                toast.success('Kod zastosowany! Kliknij "Zapisz" aby zachować.');
              }}
            />
          </div>
        )}

        {/* SEO Tool */}
        {activePanel === 'seo' && currentFile && (
          <div className="w-96 border-l border-gray-200 flex-shrink-0">
            <SEOTool
              code={code}
              filename={currentFile.name}
              onUpdateCode={(newCode) => {
                setCode(newCode);
                toast.success('Kod zaktualizowany! Kliknij "Zapisz" aby zachować.');
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Main App
function App() {
  const [user, setUser] = useState(localStorage.getItem('wp_user') || null);
  const [view, setView] = useState('loading');
  const [connections, setConnections] = useState([]);
  const [activeConnection, setActiveConnection] = useState(null);

  useEffect(() => {
    if (user) {
      loadAndAutoConnect();
    }
  }, [user]);

  const loadAndAutoConnect = async () => {
    try {
      const response = await axios.get(`${API}/connections`);
      setConnections(response.data);
      setView('dashboard');
    } catch (error) {
      console.error('Error loading connections');
      setView('dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('wp_user');
    setUser(null);
    setActiveConnection(null);
    setView('loading');
  };

  const deleteConnection = async (id) => {
    if (id === 'default-kurdynowski') {
      toast.error('Nie można usunąć domyślnego połączenia');
      return;
    }
    if (!window.confirm('Na pewno usunąć to połączenie?')) return;
    try {
      await axios.delete(`${API}/connections/${id}`);
      toast.success('Połączenie usunięte');
      loadAndAutoConnect();
    } catch (error) {
      toast.error('Błąd usuwania');
    }
  };

  if (!user) {
    return (
      <TooltipProvider>
        <div className="min-h-screen" data-testid="app-root">
          <Toaster position="top-center" richColors />
          <LoginScreen onLogin={(u) => { setUser(u); }} />
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen" data-testid="app-root">
        <Toaster position="top-center" richColors />

        {view === 'loading' && (
          <div className="min-h-screen flex items-center justify-center gradient-soft">
            <div className="text-center animate-fadeIn">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
              <p className="text-gray-600">Łączenie ze stroną...</p>
            </div>
          </div>
        )}

        {view === 'dashboard' && (
          <Dashboard
            connections={connections}
            onSelectConnection={(conn) => { setActiveConnection(conn); setView('editor'); }}
            onAddConnection={() => setView('wizard')}
            onDeleteConnection={deleteConnection}
            user={user}
            onLogout={handleLogout}
          />
        )}

        {view === 'wizard' && (
          <ConnectionWizard
            onComplete={() => { loadAndAutoConnect(); }}
            onCancel={() => setView(connections.length > 0 ? 'dashboard' : 'dashboard')}
          />
        )}

        {view === 'editor' && activeConnection && (
          <Editor
            connection={activeConnection}
            onBack={() => { setActiveConnection(null); setView('dashboard'); }}
            user={user}
            onLogout={handleLogout}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

export default App;
